/**
 * AnalyticsApiService
 *
 * HTTP service for fetching analytics data from the dashboard backend.
 * Used by the analytics dashboard component to display stored analytics.
 */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EntityService } from '@app/core/services';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

export interface AnalyticsSummaryResponse {
    success: boolean;
    data: {
        totalEvents: number;
        eventBreakdown: Record<string, number>;
        activeUsers: Array<{
            _id: string;
            userName: string;
            userEmail: string;
            eventCount: number;
            searchCount: number;
            cartAddCount: number;
            checkoutCount: number;
            proceedToBuyCount: number;
            proceedToBuySuccess?: number;
            lastActivity: string;
        }>;
        supplierBreakdown: Array<{
            _id: string;
            totalEvents: number;
            searches: number;
            cartAdds: number;
            checkouts: number;
        }>;
        dailyTrend: Array<{
            _id: string;
            totalEvents: number;
            uniqueUsers: number;
            searches: number;
            cartAdds: number;
            checkouts: number;
        }>;
        funnel: {
            searches: number;
            results: number;
            cartAdds: number;
            checkoutClicks: number;
            proceedToBuy: number;
            poGenerated: number;
            buySuccess: number;
            proceedToBuySuccess?: number;
            cartAbandons: number;
        };
    };
}

export interface AnalyticsEventsResponse {
    success: boolean;
    data: {
        events: any[];
        pagination: {
            page: number;
            pageSize: number;
            total: number;
            totalPages: number;
        };
    };
}

export interface AnalyticsSessionsResponse {
    success: boolean;
    data: {
        sessions: any[];
        pagination: {
            page: number;
            pageSize: number;
            total: number;
            totalPages: number;
        };
    };
}

@Injectable()
export class AnalyticsApiService {
    private apiUrl: string;

    constructor(
        private http: HttpClient,
        private entityService: EntityService
    ) {
        this.apiUrl =
            (environment as any).dashboard?.url +
            (environment as any).dashboard?.path;
    }

    /**
     * Get HTTP headers with entity ID for analytics API calls
     * Returns headers only if entityId is provided
     */
    private getHeaders(entityId?: string): HttpHeaders | undefined {
        if (entityId && entityId.trim()) {
            return new HttpHeaders({
                'x-entity-ids': entityId
            });
        }
        // Return undefined if no entityId - backend will use allCompanies query param instead
        return undefined;
    }

    /**
     * Fetch analytics summary (event counts, user activity, supplier breakdown, daily trend, funnel).
     */
    getSummary(params: {
        startDate?: string;
        endDate?: string;
    } = {}, entityId?: string): Observable<AnalyticsSummaryResponse['data']> {
        let httpParams = new HttpParams();
        if (params.startDate) httpParams = httpParams.set('startDate', params.startDate);
        if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);

        // If no entityId provided or empty string, add allCompanies=true to get aggregated data
        if (!entityId || !entityId.trim()) {
            httpParams = httpParams.set('allCompanies', 'true');
        }

        const headers = this.getHeaders(entityId);
        return this.http
            .get<AnalyticsSummaryResponse['data']>(`${this.apiUrl}purchase-manager/analytics/summary`, { params: httpParams, headers });
    }

    getAnalyticsDashboardData(params: { startDate?: string; endDate?: string; } = {}, entityId?: string): Observable<any> {
        let httpParams = new HttpParams();
        if (params.startDate) httpParams = httpParams.set('startDate', params.startDate);
        if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);
        // If caller explicitly requested allCompanies, honor it; otherwise attach companies if entityId provided
        if ((params as any).allCompanies) {
            httpParams = httpParams.set('allCompanies', 'true');
        } else if (entityId?.trim()) {
            httpParams = httpParams.set('companies', entityId);
        }

        // const headers = this.getHeaders(entityId);
        return this.http.get<any>(`${this.apiUrl}purchase-manager/analytics/dashboard`, { params: httpParams })
    }

    /**
     * Fetch paginated analytics events.
     */
    getEvents(params: {
        page?: number;
        pageSize?: number;
        eventType?: string;
        startDate?: string;
        endDate?: string;
        userId?: string;
        supplier?: string;
        sessionId?: string;
    } = {}, entityId?: string): Observable<AnalyticsEventsResponse['data']> {
        let httpParams = new HttpParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                httpParams = httpParams.set(key, String(value));
            }
        });

        // If no entityId provided or empty string, add allCompanies=true to get aggregated data
        if (!entityId || !entityId.trim()) {
            httpParams = httpParams.set('allCompanies', 'true');
        }

        const headers = this.getHeaders(entityId);
        return this.http
            .get<AnalyticsEventsResponse['data']>(`${this.apiUrl}purchase-manager/analytics/events`, { params: httpParams, headers });
    }

    /**
     * Fetch paginated analytics sessions.
     */
    getSessions(params: {
        page?: number;
        pageSize?: number;
        startDate?: string;
        endDate?: string;
        userId?: string;
    } = {}, entityId?: string): Observable<AnalyticsSessionsResponse['data']> {
        let httpParams = new HttpParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                httpParams = httpParams.set(key, String(value));
            }
        });

        // If no entityId provided or empty string, add allCompanies=true to get aggregated data
        if (!entityId || !entityId.trim()) {
            httpParams = httpParams.set('allCompanies', 'true');
        }

        const headers = this.getHeaders(entityId);
        return this.http
            .get<AnalyticsSessionsResponse['data']>(`${this.apiUrl}purchase-manager/analytics/sessions`, { params: httpParams, headers });
    }

    /**
     * Fetch aggregated search keywords from sync batches.
     * Returns searchKeywords stored at the batch level.
     */
    getSearchKeywords(params: {
        startDate?: string;
        endDate?: string;
    } = {}, entityId?: string): Observable<any> {
        let httpParams = new HttpParams();
        if (params.startDate) httpParams = httpParams.set('startDate', params.startDate);
        if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);

        // If no entityId provided or empty string, add allCompanies=true to get aggregated data
        if (!entityId || !entityId.trim()) {
            httpParams = httpParams.set('allCompanies', 'true');
        }

        const headers = this.getHeaders(entityId);
        return this.http
            .get<any>(`${this.apiUrl}purchase-manager/analytics/search-keywords`, { params: httpParams, headers });
    }

    // fetchSearchInsights(params: { startDate?: string; endDate?: string; } = {}, activeTab: string, entityId?: string): Observable<any> {
    fetchSearchInsights(searchInsightsQueryParams: any, entityId: string): Observable<any> {
        let httpParams = new HttpParams();
        if (searchInsightsQueryParams.startDate) httpParams = httpParams.set('startDate', searchInsightsQueryParams.startDate);
        if (searchInsightsQueryParams.endDate) httpParams = httpParams.set('endDate', searchInsightsQueryParams.endDate);
        // If caller explicitly requested allCompanies, honor it; otherwise attach companies if entityId provided
        if (searchInsightsQueryParams.allCompanies) {
            httpParams = httpParams.set('allCompanies', 'true');
        } else if (entityId?.trim()) {
            httpParams = httpParams.set('companies', entityId);
        }
        if (searchInsightsQueryParams.search.trim()) {
            httpParams = httpParams.set('search', searchInsightsQueryParams.search);
        }

        httpParams = httpParams.set('tab', searchInsightsQueryParams.tab.replace('-', '_'));
        httpParams = httpParams.set('page', searchInsightsQueryParams.page);
        httpParams = httpParams.set('pageSize', searchInsightsQueryParams.pageSize);


        const headers = this.getHeaders(searchInsightsQueryParams.entityId);

        return this.http.get<any>(`${this.apiUrl}purchase-manager/analytics/search-insights`, { params: httpParams, headers });
    }

    fetchChartData(chartQueryParams: any) {
        let httpParams = new HttpParams();
        if (chartQueryParams.startDate) httpParams = httpParams.set('startDate', chartQueryParams.startDate);
        if (chartQueryParams.endDate) httpParams = httpParams.set('endDate', chartQueryParams.endDate);
        // If caller explicitly requested allCompanies, honor it; otherwise attach companies if entityId provided
        if (chartQueryParams.allCompanies) {
            httpParams = httpParams.set('allCompanies', 'true');
        } else if (chartQueryParams.entityId?.trim()) {
            httpParams = httpParams.set('companies', chartQueryParams.entityId);
        }

        return this.http.get<any>(`${this.apiUrl}purchase-manager/analytics/dashboard`, { params: httpParams });
    }

    fetchOrderLogs(orderLogsQueryParams: any, entityId: string): Observable<any> {
        let httpParams = new HttpParams();
        if (orderLogsQueryParams.startDate) httpParams = httpParams.set('startDate', orderLogsQueryParams.startDate);
        if (orderLogsQueryParams.endDate) httpParams = httpParams.set('endDate', orderLogsQueryParams.endDate);
        // If caller explicitly requested allCompanies, honor it; otherwise attach companies if entityId provided
        if (orderLogsQueryParams.allCompanies) {
            httpParams = httpParams.set('allCompanies', 'true');
        } else if (entityId?.trim()) {
            httpParams = httpParams.set('companies', entityId);
        }
        if (orderLogsQueryParams.search.trim()) {
            httpParams = httpParams.set('search', orderLogsQueryParams.search);
        }

        httpParams = httpParams.set('tab', orderLogsQueryParams.tab);
        httpParams = httpParams.set('page', orderLogsQueryParams.page);
        httpParams = httpParams.set('pageSize', orderLogsQueryParams.pageSize);
        return this.http.get<any>(`${this.apiUrl}purchase-manager/analytics/order-logs`, { params: httpParams });
    }

    fetchUserBehaviorInsights(userBehaviorInsightsQueryParams: any, entityId: string): Observable<any> {
        let httpParams = new HttpParams();
        if (userBehaviorInsightsQueryParams.startDate) httpParams = httpParams.set('startDate', userBehaviorInsightsQueryParams.startDate);
        if (userBehaviorInsightsQueryParams.endDate) httpParams = httpParams.set('endDate', userBehaviorInsightsQueryParams.endDate);

        if (userBehaviorInsightsQueryParams.allCompanies) {
            httpParams = httpParams.set('allCompanies', 'true');
        } else if (entityId?.trim()) {
            httpParams = httpParams.set('companies', entityId);
        }

        if (userBehaviorInsightsQueryParams.search.trim()) {
            httpParams = httpParams.set('search', userBehaviorInsightsQueryParams.search);
        }

        httpParams = httpParams.set('page', userBehaviorInsightsQueryParams.page);
        httpParams = httpParams.set('pageSize', userBehaviorInsightsQueryParams.pageSize);

        return this.http.get<any>(`${this.apiUrl}purchase-manager/analytics/user-behavior`, { params: httpParams });
    }

    /**
     * Fetch aggregated cart products from sync batches.
     * Returns cartProducts stored at the batch level.
     */
    getCartProducts(params: {
        startDate?: string;
        endDate?: string;
    } = {}, entityId?: string): Observable<any> {
        let httpParams = new HttpParams();
        if (params.startDate) httpParams = httpParams.set('startDate', params.startDate);
        if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);

        // If caller explicitly requested allCompanies, honor it; otherwise attach companies if entityId provided
        if ((params as any).allCompanies) {
            httpParams = httpParams.set('allCompanies', 'true');
        } else if (entityId?.trim()) {
            httpParams = httpParams.set('companies', entityId);
        }

        const headers = this.getHeaders(entityId);
        return this.http
            .get<any>(`${this.apiUrl}purchase-manager/analytics/cart-products`, { params: httpParams, headers });
    }
}
